/**
 * The game runtime — owns THE GameState, the online tick interval, autosave,
 * and offline catch-up. Scenes and DOM UI subscribe to events + read state;
 * they never own or mutate it (ADR-002). One instance per page.
 */

import { SAVE, TICKS } from "@/data/tunables";
import { deaDanger, heatDanger } from "@/systems/meters";
import type { GameState } from "@/state/GameState";
import { loadGame, saveGame, SaveError } from "@/saves/SaveManager";
import { offlineTicks, tick, type TickEvent } from "@/systems/engine";
import { criminalEarnedOffline } from "@/systems/criminal";
import type { GameOverReason } from "@/systems/meters";

export type EventListener = (events: TickEvent[]) => void;

export interface OfflineReport {
  ticks: number;
  /** Real wall-clock ms away (pre-cap) — the report shows a human duration. */
  awayMs: number;
  fundingEarned: number;
  /** v0.15 G5: of fundingEarned, how much came from "the other business" (the
   *  criminal track) — the honest breakout Russell asked for. 0 when the track
   *  is locked. Upgrades don't change offline, so this is income/tick × ticks. */
  criminalEarned: number;
  graduated: number;
  /** v0.13.2: promotions up the continuum while away. */
  promoted: number;
  died: number;
  relapsed: number;
  /** v0.13.2 diagnostics: care state AT RETURN — beds occupied + leads waiting
   *  to be admitted. Together with completions these show WHERE offline income
   *  came from (and expose bed-drain / manual-admit stall). */
  bedsOccupied: number;
  queueWaiting: number;
  /** Set if a meter game-over forced a prestige during offline catch-up (v0.5). */
  gameOver: { reason: GameOverReason; payout: number } | null;
  /** v0.8: a meter sits in the danger zone after catch-up — return informed. */
  heatHigh: boolean;
  deaHigh: boolean;
}

class Runtime {
  state!: GameState;
  /** Set when load failed — the boot scene shows the fatal panel. */
  loadError: SaveError | null = null;
  offlineReport: OfflineReport | null = null;
  /** True when boot started a brand-new game (no save existed) — v0.12
   *  telemetry's fresh-vs-returning signal. */
  wasFresh = false;

  private listeners = new Set<EventListener>();
  private intervalId: number | null = null;

  /** Stop the tick loop + listeners (tests/teardown). */
  stop(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    document.removeEventListener("visibilitychange", this.onHide);
  }
  private ticksSinceSave = 0;
  private savePending = false;
  private lastTickAt = 0;
  private saveFailureSurfaced = false;
  /** UI hook (wired by main.ts) — keeps this module free of ui imports. */
  onSaveFailure: (() => void) | undefined;

  /** Persist, surfacing failure LOUDLY once (quota/private mode) — a game
   *  that silently stops saving is the exact failure ADR-003 forbids. */
  private saveSafe(): void {
    try {
      saveGame(this.state);
      this.saveFailureSurfaced = false;
    } catch (err) {
      console.error("halfway: save failed", err);
      if (!this.saveFailureSurfaced) {
        this.saveFailureSurfaced = true;
        this.onSaveFailure?.();
      }
    }
  }

  boot(): void {
    try {
      const loaded = loadGame(Date.now() & 0x7fffffff);
      this.state = loaded.state;
      this.wasFresh = loaded.fresh;
      if (!loaded.fresh) {
        const dt = offlineTicks(loaded.offlineMs);
        if (dt > 0) {
          const before = this.state.run.funding;
          const result = tick(this.state, dt, { liveEvents: false }); // v0.13: event clocks freeze offline
          // Away-report carries OUTCOMES too — an offline death (or a meter
          // game-over) must not be silently absorbed (leg-1 + v0.5 findings).
          const forced = result.events.find((e) => e.type === "gameOver");
          const gameOver = forced?.type === "gameOver" ? { reason: forced.reason, payout: forced.payout } : null;
          this.offlineReport = {
            ticks: dt,
            awayMs: loaded.offlineMs,
            // A forced prestige reset the run, so the funding delta is meaningless.
            // SIGNED (v0.15 bot leg): a fixer's offline bribes can exceed income,
            // so a genuine net LOSS must show as negative, not be clamped to 0.
            fundingEarned: gameOver ? 0 : Math.floor(this.state.run.funding - before),
            // GROSS criminal income over the window (v0.15 G5). Shown as its
            // own labelled line, NOT a subset of Net earned — with a fixer
            // active the bribes make net < gross, so the report calls it gross.
            criminalEarned: gameOver ? 0 : criminalEarnedOffline(this.state, dt),
            graduated: result.events.filter((e) => e.type === "graduated").length,
            promoted: result.events.filter((e) => e.type === "promoted").length,
            died: result.events.filter((e) => e.type === "died").length,
            relapsed: result.events.filter((e) => e.type === "relapsed").length,
            bedsOccupied: this.state.run.patients.length,
            queueWaiting: this.state.run.queue.length,
            gameOver,
            // a meter sitting in the danger zone after catch-up (not reset by a
            // game-over) — surface it so the player returns informed (v0.8).
            heatHigh: !gameOver && heatDanger(this.state),
            deaHigh: !gameOver && deaDanger(this.state),
          };
          // Offline payouts are priced at the offline tick rate — clearing the
          // log keeps NET/SEC (an online-rate display) honest post-catch-up.
          this.state.run.incomeLog = [];
        }
      }
    } catch (err) {
      if (err instanceof SaveError) {
        this.loadError = err;
        return; // no interval; boot scene handles the panel
      }
      throw err;
    }

    this.lastTickAt = Date.now();
    this.intervalId = window.setInterval(() => this.onlineTick(), TICKS.onlineMs);
    document.addEventListener("visibilitychange", this.onHide);
  }

  private onlineTick(): void {
    // Hidden-tab interval throttling leaves a gap; make it up in one call so
    // a backgrounded (not closed) tab doesn't fall into a dead zone.
    const now = Date.now();
    const gapTicks = Math.min(600, Math.floor((now - this.lastTickAt) / TICKS.onlineMs));
    this.lastTickAt = now;
    // v0.13 (bot leg): a hidden-tab catch-up batch is UNWATCHABLE — the
    // online-only ruling ("can't click it") applies, so event clocks
    // freeze for any batch bigger than one live tick.
    // Events run only for a single LIVE tick on a VISIBLE tab — a hidden
    // tab still fires throttled ~1/s intervals (gapTicks can be 1), and a
    // card you can't see must not age/expire (bot leg P1, online-only).
    const visible = typeof document === "undefined" || document.visibilityState === "visible";
    const result = tick(this.state, Math.max(1, gapTicks), { liveEvents: gapTicks <= 1 && visible });
    for (const l of this.listeners) l(result.events);
    this.ticksSinceSave += 1;
    if (this.ticksSinceSave >= SAVE.autosaveEveryTicks) {
      this.ticksSinceSave = 0;
      this.scheduleSave();
    }
  }

  /** Debounced save off the hot path (ADR-003 as amended). */
  private scheduleSave(): void {
    if (this.savePending) return;
    this.savePending = true;
    const write = () => {
      this.savePending = false;
      this.saveSafe();
    };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(write, { timeout: 2_000 });
    } else {
      window.setTimeout(write, 0);
    }
  }

  /** Tab-hide save is IMMEDIATE — it's the one that survives iOS tab-kills. */
  private onHide = (): void => {
    if (document.visibilityState === "hidden" && this.state) {
      this.saveSafe();
    }
  };

  /** Perform a player action then notify listeners of its events immediately. */
  act(events: TickEvent | TickEvent[]): void {
    const list = Array.isArray(events) ? events : [events];
    for (const l of this.listeners) l(list);
    this.scheduleSave();
  }

  subscribe(listener: EventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

/** The one runtime. Import and use; boot() is called once from main.ts. */
export const runtime = new Runtime();
